import type { PureFunction } from '@bonsai-components/utility-types';
import { focusRingStyles } from '@moderneinc/design-system-components';
import {
  borderRadius,
  breakpoints,
  colors,
  semanticColors,
  semanticColorsDark,
  semanticColorsLight,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import {
  createTheme,
  dialogClasses,
  inputBaseClasses,
  type PaletteMode,
  type PaletteOptions,
  paperClasses,
  responsiveFontSizes,
  type Theme,
  type ThemeOptions
} from '@mui/material';
import { gridClasses } from '@mui/x-data-grid-pro';
import { pickersInputBaseClasses } from '@mui/x-date-pickers/PickersTextField';
import { ignorePasswordManagers } from '../constants/forms';
import { lineHeight } from '../constants/typography';
import { ModMuiCheckbox } from './component-defaults/mod-mui-checkbox';

export const moderneColors = {
  modGrey: colors.grey,
  modDigitalBlue: colors.digitalBlue,
  modDigitalGreen: colors.digitalGreen,
  modTealGreen: colors.tealGreen,
  modRed: colors.red,
  modOrange: colors.orange,
  modGold: colors.gold,
  modViolet: colors.violet,
  /**
   * @deprecated Use mod color alternatives
   */
  newGreys: {
    15: '#F4F4F4',
    50: '#edeeef',
    100: '#c6c9cd',
    200: '#aaafb5',
    300: '#838a93',
    400: '#6b737e',
    500: '#46505e',
    600: '#404956',
    700: '#323943',
    800: '#272c34',
    900: '#1d2227',
    1000: '#000000'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  grey: {
    100: '#F8F9FA',
    150: '#EDEFEF', // source GitHub markdown background
    200: '#E9ECEF',
    300: '#DEE2E6',
    400: '#CED4DA',
    500: '#ADB5BD',
    600: '#6C757D',
    700: '#495057',
    800: '#343A40',
    900: '#272C31'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  red: {
    main: '#FF3232',
    50: '#FFEBEB',
    100: '#FFBFBF',
    200: '#FFA1A1',
    300: '#FF7676',
    400: '#FF5B5B',
    500: '#FF3232',
    600: '#E82E2E',
    700: '#B52424',
    800: '#8C1C1C',
    900: '#6B1515'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  green: {
    main: '#88FE9B',
    50: '#F3FFF5',
    100: '#DAFFE0',
    200: '#C8FFD1',
    300: '#AFFEBC',
    400: '#A0FEAF',
    500: '#88FE9B',
    600: '#7CE78D',
    700: '#61B46E',
    800: '#4B8C55',
    900: '#396B41'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  blue: {
    iceBlue: '#00FFF0', // originally used for login orb in dark mode
    main: colors.digitalBlue[500],
    50: '#EAECFF',
    100: '#BFC4FF',
    200: '#9FA8FF',
    300: '#7480FF', // required for the ColorPalette type
    400: '#5968FF', // required for the ColorPalette type
    500: '#2F42FF',
    600: '#2B3CE8',
    700: '#212FB5',
    800: '#1A248C',
    900: '#141C6B'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  indigo: {
    midnight: '#00193D',
    main: '#00193D',
    50: '#F4F7FB',
    100: '#E3E5E9',
    150: '#C4C4C4',
    200: '#AAB2BE',
    300: '#717F93',
    400: '#394C67',
    450: '#324054',
    500: '#121214',
    700: '#000B1B',
    800: '#00060D'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  violet: {
    main: '#461DDE',
    50: '#EDE8FC',
    100: '#C6B9F5',
    200: '#AA97F0',
    300: '#8368E9',
    400: '#6B4AE5',
    500: '#461DDE', // new violet color
    600: '#401ACA',
    700: '#32159E',
    800: '#27107A',
    900: '#1D0C5D'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  roseate: {
    recipeRunPurple: '#9A25BB',
    main: '#DB4197',
    50: '#FBECF5',
    100: '#F4C4DF',
    200: '#EEA8CF',
    300: '#E780B9',
    400: '#E267AC',
    500: '#DB4197',
    600: '#C73B89',
    700: '#9B2E6B',
    800: '#782453',
    900: '#5C1B3F'
  },
  /**
   * @deprecated Use mod color alternatives
   */
  black: {
    100: '#1E1E1E',
    800: '#121212'
  }
} as const;

export const textColor =
  (emphasis: 'high' | 'medium' = 'medium') =>
  (theme: Theme): string => {
    if (emphasis === 'high') {
      return theme.palette.mode === 'light'
        ? theme.palette.indigo.main
        : theme.palette.text.primary;
    }

    return theme.palette.mode === 'light'
      ? theme.palette.grey[700]
      : theme.palette.grey[500];
  };

export const backgroundColor =
  (emphasis: 'high' | 'medium' = 'medium') =>
  (theme: Theme): string => {
    if (emphasis === 'high') {
      return theme.palette.mode === 'light'
        ? theme.palette.common.white
        : theme.palette.black[800];
    }

    return theme.palette.mode === 'light'
      ? theme.palette.grey[200]
      : theme.palette.black[100];
  };

// MUI X derives the grid surface from `background.paper` and, in dark mode,
// lightens it 5% (color-mix with #fff) — which washed our #121212 paper into a
// #1E1E1E row. Feeding the design-system data-grid tokens through
// `palette.DataGrid` pins the surface to the exact design-system value and
// skips that lightening.
// Flipping CSS vars, so one definition serves both modes.
const dataGridSurfaces = {
  bg: semanticColors.color.surface.dataGrid.default,
  headerBg: semanticColors.color.surface.dataGrid.header,
  pinnedBg: semanticColors.color.surface.dataGrid.default
};

const lightPalette = {
  background: {
    default: semanticColors.color.surface.page,
    paper: semanticColors.color.surface.raised,
    defaultChannel: undefined,
    paperChannel: undefined
  },
  DataGrid: dataGridSurfaces,
  default: {},
  primary: {
    main: semanticColorsLight.button.primary.bg,
    dark: semanticColorsLight.button.primary.bgPressed
  },
  secondary: {
    main: moderneColors.grey['900']
  },
  text: {
    primary: moderneColors.grey['800'],
    secondary: moderneColors.indigo.main
  },
  error: {
    main: semanticColorsLight.color.status.error.solid,
    light: semanticColorsLight.color.status.error.bg,
    dark: semanticColorsLight.color.status.error.fg
  },
  info: {
    main: semanticColorsLight.color.status.info.solid
  },
  warning: {
    main: semanticColorsLight.color.status.warning.solid
  },
  success: {
    main: semanticColorsLight.color.status.success.solid
  },
  diffRemoval: {
    main: moderneColors.red['100'],
    light: moderneColors.red['50']
  },
  diffAddition: {
    main: moderneColors.green['100'],
    light: moderneColors.green['50']
  },
  diffHeader: {
    main: moderneColors.newGreys['15']
  },
  // `main` highlights the matched words; rows use `surface.highlight`, as code search does
  searchMarker: {
    main: moderneColors.modGold['300'],
    contrastText: moderneColors.grey['800']
  }
};

const darkPalette = {
  background: {
    // Same design-system tokens as lightPalette (flipping vars). The old black[100]/
    // black[800] were unmigrated placeholders: they left the page at #1E1E1E
    // and inverted elevation (menus/dialogs darker than the page they sit on).
    default: semanticColors.color.surface.page,
    paper: semanticColors.color.surface.raised,
    defaultChannel: undefined,
    paperChannel: undefined
  },
  DataGrid: dataGridSurfaces,
  default: {
    main: moderneColors.grey[500]
  },
  primary: {
    main: moderneColors.indigo[100]
  },
  secondary: {
    main: moderneColors.grey[200]
  },
  // WARNING: dark hexes, not the flipping `semanticColors` var `background`
  // uses — `cssVariables` feeds these to setColorChannel, and alpha() throws
  // on a var(). (#8869)
  text: {
    primary: semanticColorsDark.color.text.primary,
    secondary: semanticColorsDark.color.text.secondary
  },
  error: {
    main: semanticColorsDark.color.status.error.solid,
    light: semanticColorsDark.color.status.error.bg,
    dark: semanticColorsDark.color.status.error.fg
  },
  info: {
    main: semanticColorsDark.color.status.info.solid
  },
  warning: {
    main: semanticColorsDark.color.status.warning.solid
  },
  diffRemoval: {
    main: '#4C1B1D',
    light: '#2A1518'
  },
  diffAddition: {
    main: '#183C1F',
    light: '#0F2117'
  },
  diffHeader: {
    main: '#111C32'
  },
  searchMarker: {
    main: moderneColors.modGold['600'],
    contrastText: moderneColors.grey['900']
  }
};

const getPalette = (mode: PaletteMode): PaletteOptions => {
  if (mode === 'dark') {
    return {
      mode,
      ...darkPalette,
      ...moderneColors
    };
  } else {
    return {
      mode,
      ...lightPalette,
      ...moderneColors
    };
  }
};

/**
 * MuiDataGrid theme configuration has been moved to ModerneDataGrid component
 * @see /components/styled-components/data-grids/data-grids.styled.tsx
 *
 * Exception: the grid container surface radius stays here (see the MuiDataGrid
 * override below). It has to reach the design-system grids (ModDataGrid /
 * ModInfiniteScrollGrid / ModPaginatedGrid) too, and those bypass
 * ModerneDataGrid — the theme root override is the only lever common to both.
 */

type ModerneThemeConfiguration = {
  mode: PaletteMode;
  customFontFamily?: ModerneCustomFontFamilies;
};

const getThemeConfiguration: PureFunction<
  ModerneThemeConfiguration,
  ThemeOptions
> = ({ mode, customFontFamily }) => ({
  cssVariables: true,
  palette: getPalette(mode),
  spacing: spacing.spacing_1,
  /**
   * Semantic z-index values to prevent nested dialog conflicts.
   *
   * Layer hierarchy:
   * - Navigation (1400): Global nav menus, org selector
   * - Dialogs (1450+): Modal dialogs with clear nesting levels
   * - Popovers (1500): Dropdowns, autocompletes, tooltips
   *
   * @see https://github.com/moderneinc/moderne-ui/issues/6956
   */
  zIndex: {
    // MUI defaults (keep all existing values)
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,

    // Semantic dialog layers (new)
    dialog: 1450, // Standard dialogs (above navigation)
    dialogNested: 1460 // Nested dialogs (confirmations, child dialogs)
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: breakpoints.sm,
      md: breakpoints.md,
      lg: breakpoints.lg,
      xl: breakpoints.xl
    }
  },
  typography: {
    fontFamily: customFontFamily?.body || typography.fontFamily.body,
    fontFamilyMonospace:
      customFontFamily?.monospace || `${typography.fontFamily.code}, monospace`,
    h1: {
      fontSize: typography.fontSize.h1,
      lineHeight: lineHeight.xl / typography.fontSize.h1,
      fontWeight: typography.fontWeight.regular
    },
    h2: {
      fontSize: typography.fontSize.h2,
      lineHeight: lineHeight.xl / typography.fontSize.h2,
      fontWeight: typography.fontWeight.regular
    },
    h3: {
      fontSize: typography.fontSize.h3,
      lineHeight: lineHeight.xl / typography.fontSize.h3,
      fontWeight: typography.fontWeight.regular
    },
    h4: {
      fontSize: typography.fontSize.h4,
      lineHeight: lineHeight.xl / typography.fontSize.h4,
      fontWeight: typography.fontWeight.regular
    },
    h5: undefined,
    h6: undefined,
    body: {
      fontSize: typography.fontSize.default,
      fontWeight: typography.fontWeight.regular,
      lineHeight: lineHeight.m / typography.fontSize.default
    },
    bodySm: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.regular,
      lineHeight: lineHeight.xs / typography.fontSize.sm
    },
    // deprecated
    body1: {
      fontSize: '1rem' // 16px,
    },
    // deprecated
    body2: {
      fontSize: '0.875rem' // 14px,
    }
  },
  components: {
    // MUI makes this slot a scroll container, so a ~1px text overflow paints a
    // full scrollbar. `overflow-wrap` keeps long tokens in the border (#8830).
    MuiAlert: {
      styleOverrides: {
        message: {
          overflow: 'visible',
          overflowWrap: 'anywhere'
        }
      }
    },
    // Disable MUI's dark-mode elevation overlay. In dark, MUI paints a
    // translucent-white `background-image: var(--Paper-overlay)` on every
    // Paper/Card to fake elevation, which lightens our explicit design-system
    // surface tokens (e.g. a #26262e card no longer reads as #26262e). The
    // design system expresses depth with discrete surface tokens +
    // borders/shadows, not the overlay. Light-safe: MUI only applies the
    // overlay in dark mode.
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    // Cross-cutting grid surface styling that must reach the design-system grids too
    // (they bypass ModerneDataGrid): a 12px rounded container (mock deploy/table
    // cards, overflow-clipped to the corners) and monospace cells for columns
    // that opt in with `cellClassName: 'monospace'` (versions, names, counts).
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: borderRadius.card,
          overflow: 'hidden',
          [`& .monospace .${gridClasses.cell}, & .monospace.${gridClasses.cell}`]:
            {
              fontFamily: theme.typography.fontFamilyMonospace,
              // Versions and artifact coordinates break the way identifiers do,
              // so an inserted hyphen reads as part of the value. SmartBreak
              // exempts the columns that route through it; this covers the
              // monospace columns that do not (recipe artifacts, connector ids).
              hyphens: 'none'
            },
          // Quick-search input sits 34px tall with 13px text, not the design-system
          // 40px/16px default (mock .cl-search). 34px and 13px are not spacing
          // or type tokens. Zero the vertical padding so the icon still fits
          // once the field is shorter.
          [`& .${gridClasses.toolbar} .${inputBaseClasses.root}`]: {
            height: '34px',
            paddingTop: 0,
            paddingBottom: 0,
            fontSize: '13px'
          }
        })
      }
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body: 'p',
          bodySm: 'p'
        }
      }
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // ButtonBase doesn't pick up `typography.fontFamily`, so Button /
          // IconButton / MenuItem / Tab fall back to the generic sans stack
          // instead of the app's body font.
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          '&:focus-visible': {
            ...focusRingStyles
          }
        }
      }
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true
      },
      styleOverrides: {
        root: {
          borderRadius: 100,
          textTransform: 'none'
        }
      }
    },
    MuiList: {
      defaultProps: {
        dense: true,
        disablePadding: true
      }
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover'
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        // MUI defaults these to `text.secondary`; labels want full-strength ink.
        root: {
          color: semanticColors.color.text.inputLabel
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        /**
         * HACK: Fixes autofill background color in Chrome
         * @see https://github.com/mui/material-ui/issues/44665
         */
        input: {
          '&:-webkit-autofill': {
            WebkitBoxShadow: 'unset',
            WebkitTextFillColor: 'unset',
            caretColor: 'unset'
          }
        }
      }
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        // `slotProps` is deep-merged by MUI's defaultProps resolution (unlike
        // `inputProps`), so this reaches every TextField's <input> even when a
        // call site sets its own (possibly partial) `slotProps.htmlInput`.
        slotProps: { htmlInput: ignorePasswordManagers }
      }
    },
    MuiDialogTitle: {
      defaultProps: {
        variant: 'h4'
      }
    },
    MuiDialogContent: {
      defaultProps: {
        dividers: true
      }
    },
    MuiDialog: {
      styleOverrides: {
        // Darker, more defined border on every modal. The extra class
        // selector outweighs ModModal's own `& .MuiDialog-paper` rule
        // (which paints a light border.primary), without reaching into a
        // design-system component per-usage.
        paper: {
          [`&.${dialogClasses.paper}.${paperClasses.root}`]: {
            border: `1px solid ${semanticColors.color.border.strong}`
          },
          // The paper is a flex column, and that is what bounds DialogContent's
          // height. Dialogs that wrap their body in a <form> put a block box in
          // between, which breaks the chain: the content never clamps, so it
          // overflows the paper instead of scrolling. Enrol the form in the
          // column. `min-height: 0` is the load-bearing part — without it the
          // form's automatic minimum size keeps it at min-content.
          '& > form': {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }
        }
      }
    },
    MuiTable: {
      defaultProps: {
        size: 'small',
        padding: 'none'
      }
    },
    MuiTabs: {
      defaultProps: {
        textColor: 'secondary'
      },
      styleOverrides: {
        root: {
          minHeight: 'auto'
        }
      }
    },
    MuiTab: {
      defaultProps: {
        iconPosition: 'start'
      },
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 'auto',
          textTransform: 'none',
          padding: theme.spacing(1, 2)
        })
      }
    },
    MuiInputBase: {
      defaultProps: {
        // `slotProps.input` (deep-merged, like MuiTextField's `slotProps.htmlInput`
        // above) so this reaches every InputBase consumer even when a call site
        // sets its own `inputProps` (e.g. for ARIA attributes) — InputBase spreads
        // `slotProps.input` after `inputProps`, so it always wins on conflicts.
        slotProps: { input: ignorePasswordManagers }
      }
    },
    MuiCollapse: {
      defaultProps: {
        timeout: 'auto',
        appear: true,
        mountOnEnter: true,
        unmountOnExit: true
      }
    },
    MuiPopover: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center'
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        // Lift Menu poppers above dialogs (theme.zIndex.dialog = 1450,
        // dialogNested = 1460). Without this, the column/operator dropdowns
        // inside a data grid filter panel render at z-index 1300 (modal
        // default) and slip behind a dialog hosting the grid — making
        // half the options invisible. Stays below tooltips (1500).
        root: ({ theme }) => ({
          zIndex: theme.zIndex.dialogNested
        })
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        // Autocomplete's popper defaults to zIndex.modal (1300), independent
        // of the MuiMenu override above. The "Value" field on singleSelect
        // filter columns (e.g. State) renders as an Autocomplete inside the
        // filter panel, which itself sits at dialogNested (1460) — so its
        // box-shadow was painting over the dropdown. Match MuiMenu's layer.
        popper: ({ theme }) => ({
          zIndex: theme.zIndex.dialogNested
        })
      }
    },
    MuiCheckbox: ModMuiCheckbox,
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          variants: [
            {
              props: { fontSize: 'xsmall' },
              style: {
                fontSize: '1.125rem' // 18px
              }
            }
          ]
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        // Both halves come from the tooltip pair, which inverts against the
        // page — mixing in a page-keyed token lands on the wrong side of it.
        // `palette.grey[700]` did exactly that, and couldn't flip either.
        tooltip: ({ theme }) => ({
          backgroundColor: semanticColors.color.surface.tooltip,
          color: semanticColors.color.text.tooltip,
          boxShadow: theme.shadows[4],
          '& a': {
            color: semanticColors.color.text.link
          }
        }),
        // MUI defaults the arrow to `grey[700]`, which can't flip.
        arrow: {
          color: semanticColors.color.surface.tooltip
        }
      }
    },
    MuiMultiInputDateRangeField: {
      styleOverrides: {
        root: ({ theme }) => ({
          [`& .${pickersInputBaseClasses.sectionsContainer}`]: {
            paddingTop: '8.5px',
            paddingBottom: '8.5px'
          }
        })
      }
    }
  }
});

type ModerneCustomFontFamilies = {
  body?: string;
  monospace?: string;
};

type ModerneTheme = {
  mode?: PaletteMode;
  customPalette?: Partial<PaletteOptions>;
  customFontFamily?: ModerneCustomFontFamilies;
};

export const createModerneTheme: PureFunction<ModerneTheme, Theme> = ({
  mode = 'light',
  customPalette = {},
  customFontFamily
}) => {
  return responsiveFontSizes(
    createTheme(
      getThemeConfiguration({ mode, customFontFamily }),
      { palette: customPalette },
      {
        factor: 4
      }
    )
  );
};
